import {Component, HostListener, Signal} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {RmaService} from "../../api/rma.service";
import {LoanedItem, Rma, RmaResolvedStatus, RmaStatus, RmaStatusType, ServiceLocation} from "../../api/model/rma";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {apiErrorToMessage} from "../../common/util";
import {EMPTY, merge, Observable, startWith} from "rxjs";
import _ from "lodash";
import {EquipmentCatalog} from "../../api/model/equipment-catalog";
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {map} from "rxjs/operators";
import {EquipmentCatalogService} from "../../api/equipment-catalog.service";
import {ProjectService} from "../../api/project.service";
import {EquipmentService} from "../../api/equipment.service";
import {Project} from "../../api/model/project";
import {Equipment} from "../../api/model/equipment";
import {promptDeleteMathProblem} from "../../delete-math-problem.guard";
import {ClientService} from "../../api/client.service";
import {Client, Person} from "../../api/model/client";
import {MatDialog} from "@angular/material/dialog";
import {ClientEditDialogComponent} from "../client-edit-dialog/client-edit-dialog.component";
import {AlertDetailed} from "../../api/model/alert";
import {rmaAlertsFor, rmaAlertsTooltip} from "../rma-list/rma-list.component";
import {AlertService} from "../../api/alert.service";

@Component({
  selector: 'app-single-rma',
  templateUrl: './single-rma.component.html',
  styleUrls: ['./single-rma.component.sass']
})
export class SingleRmaComponent {
  rma!: Rma;
  editForm: FormGroup = this.fb.group({});
  rmaStatusTypes: string[] = Object.values(RmaStatusType);
  apiError: string | undefined;
  allEquipment: EquipmentCatalog[] = [];
  filteredEquipment: Observable<EquipmentCatalog[]> = EMPTY;
  relatedProject: Project | undefined;
  relatedEquipment: Equipment | undefined;
  projectFirmwareVersion: string | undefined;
  currentClient: Client | null = null;
  availablePeople: Person[] = [];
  serviceLocations: ServiceLocation[] = Object.values(ServiceLocation);
  alerts: Signal<AlertDetailed[]> = this.alertService.rmaAlerts;

  constructor(
    private route: ActivatedRoute,
    private rmaService: RmaService,
    private router: Router,
    private equipmentCatalogService: EquipmentCatalogService,
    private projectService: ProjectService,
    private equipmentService: EquipmentService,
    private alertService: AlertService,
    private clientService: ClientService,
    private dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private fb: FormBuilder,
  ) {
    equipmentCatalogService.list().subscribe((eqs) => this.allEquipment = eqs);

    this.route.data.subscribe(data => {
      this.rma = data['rma'] as Rma;
      this.selectedClientId = this.rma.clientId;
      if (this.rma.projectId) {
        projectService.get(this.rma.projectId).subscribe(p => this.relatedProject = p)
        equipmentService.findBySerialNumber(this.rma.serialNumber).subscribe(r => {
          this.relatedEquipment = r.equipment[0];
          this.projectFirmwareVersion = this.relatedEquipment.items.find(i => i.serialNumber.includes(this.rma.serialNumber))?.firmwareVersion
        })
      }
      this.editForm = this.fb.group({
        rmaNumber: [{value: this.rma.rmaNumber, disabled: true}, [Validators.required]],
        serialNumber: [this.rma.serialNumber, [Validators.required]],
        manufacturer: [this.rma.manufacturer, [Validators.required]],
        sku: [this.rma.sku, [Validators.required]],
        model: [this.rma.model, [Validators.required]],
        hyperlink: [this.rma.hyperlink],
        description: [this.rma.description],
        clientName: [this.rma.clientName, [Validators.required]],
        contactPerson: [this.rma.contactPerson],
        location: [this.rma.location ?? ServiceLocation.SONUS_SUPPORT_CENTER, [Validators.required]],
        firmwareVersion: [this.rma.firmwareVersion],
        invoiceNumber: [this.rma.invoiceNumber],
        invoiceDate: [this.rma.invoiceDate],
        installedDate: [this.rma.installedDate],
        warrantyUntil: [this.rma.warrantyUntil],
        isOnHold: [this.rma.isOnHold],
        isWarranty: [this.rma.isWarranty],
        statuses: this.fb.array(this.rma.statuses.map(s => this.generateStatusForm(s))),
        loanedItems: this.fb.array(this.rma.loanedItems.map(l => this.generateLoanedItemForm(l))),
      });

      this.updateAvailablePeople();
      this.updateDisabledFields();

      this.editForm.controls['warrantyUntil'].valueChanges.subscribe((value: Date | null) => {
        if (value && new Date(value) > new Date() && this.editForm.controls['isWarranty'].pristine) {
          this.editForm.controls['isWarranty'].setValue(true);
        }
      });

      this.filteredEquipment = merge(...this.loanedItems.controls.map(group => group.controls['sku'].valueChanges))
        .pipe(
          startWith(''),
          map(state => (state ? this._filterEquipment(state).slice(0, 20) : [])),
        );
    });
  }

  private _selectedClientId: string | null = null;

  get selectedClientId(): string | null {
    return this._selectedClientId;
  }

  set selectedClientId(v: string | null) {
    const previous = this._selectedClientId;
    this._selectedClientId = v;
    if (previous !== null && previous !== v) {
      this.editForm.controls['contactPerson']?.setValue(null);
    }
    this.refreshClient();
  }

  get statuses(): FormArray<FormGroup> {
    return this.editForm.controls['statuses'] as FormArray<FormGroup>;
  }

  get loanedItems(): FormArray<FormGroup> {
    return this.editForm.controls['loanedItems'] as FormArray<FormGroup>;
  }

  get selectedContactPerson(): Person | null {
    const ctrl = this.editForm.controls['contactPerson'];
    return ctrl ? (ctrl.value as Person | null) : null;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return !this.editForm.dirty;
  }

  addStatus() {
    this.statuses.push(this.generateStatusForm());
    this.editForm.markAsDirty();
  }

  addLoanedItem() {
    this.loanedItems.push(this.generateLoanedItemForm());
    this.editForm.markAsDirty();
  }

  removeStatus(i: number) {
    if (confirm("Are you sure you want to remove this status?")) {
      this.statuses.removeAt(i);
      this.editForm.markAsDirty();
    }
  }

  removeLoanedItem(i: number) {
    if (confirm("Are you sure you want to remove this loaned item?")) {
      this.loanedItems.removeAt(i);
      this.editForm.markAsDirty();
    }
  }

  onCancel() {
    this.editForm.reset(this.rma);
    this.selectedClientId = this.rma.clientId;
    this.editForm.controls['contactPerson'].setValue(this.rma.contactPerson);
    this.updateAvailablePeople();
    this.close()
  }

  onSubmitAndClose() {
    this.onSubmit(true);
  }

  onSubmit(andClose: boolean = false) {
    this.apiError = undefined;

    const formData = this.editForm.getRawValue();
    const base = _.cloneDeep(this.rma);
    base.loanedItems = [];
    base.statuses = [];
    const request = _.merge(base, formData) as Rma;
    request.clientId = this.selectedClientId;


    if (!this.rma.isOnHold || confirm("You are editing RMA which is currently On Hold. Are you sure you want to continue?")) {
      this.rmaService.update(this.rma.id, request).subscribe({
        next: (rma) => {
          console.log("SingleRmaComponent onSubmit updated", rma);
          this.editForm.markAsPristine();
          this.apiError = undefined;
          this.rma = rma;
          this.selectedClientId = rma.clientId;
          this.editForm.controls['contactPerson'].setValue(rma.contactPerson);
          this.updateAvailablePeople();
          this.updateDisabledFields();
          if (andClose) this.close();
        },
        error: error => {
          console.log("SingleRmaComponent onSubmit error", error);
          this.apiError = apiErrorToMessage(error);
        }
      });
    }
  }

  close() {
    this.router.navigate(['/service'])
  }

  autocompleteSelect($event: MatAutocompleteSelectedEvent, form: FormGroup) {
    const found = this.allEquipment.find(value => value.id == $event.option.id)
    if (found) {
      form.controls['sku'].setValue(found.sku);
      form.controls['model'].setValue(found.model);
      form.controls['manufacturer'].setValue(found.manufacturer);
    }
  }

  totalTimeSpentInHours(): number {
    return this.statuses.controls.map(formGroup => formGroup.controls['timeSpentInHours'].value).reduce((previousValue, currentValue) => previousValue + currentValue, 0)
  }

  onDeleteClick() {
    if (promptDeleteMathProblem()) {
      this.rmaService.delete(this.rma.id).subscribe({
        next: value => {
          console.log("SingleRmaComponent deleted", this.rma.id, value);
          this.router.navigate(['/service']);
        },
        error: error => {
          console.log("SingleRmaComponent onDeleteClick error", error);
          this.apiError = apiErrorToMessage(error);
        }
      })
    }
  }

  comparePeople = (a: Person | null, b: Person | null): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.name === b.name;
  };

  personLabel(p: Person): string {
    if (p.name) return p.name;
    return p.email || p.phone || '(unnamed)';
  }

  onEditContactPersonClick() {
    if (!this.currentClient) return;
    const ref = this.dialog.open(ClientEditDialogComponent, {
      data: {client: this.currentClient},
    });
    ref.afterClosed().subscribe((updated: Client | undefined) => {
      if (!updated) return;
      this.currentClient = updated;
      const cp = this.editForm.controls['contactPerson'].value as Person | null;
      if (cp) {
        const matchingPerson = this.findMatchingPerson(updated.people, cp);
        this.editForm.controls['contactPerson'].setValue(matchingPerson ? matchingPerson : null);
        this.editForm.markAsDirty();
      }
      this.updateAvailablePeople();
    });
  }

  alertsFor(rma: Rma, alerts: AlertDetailed[]): AlertDetailed[] | undefined {
    return rmaAlertsFor(rma, alerts);
  }

  alertsTooltip(rma: Rma, alerts: AlertDetailed[]): string {
    return rmaAlertsTooltip(rma, alerts);
  }

  private _filterEquipment(filter: string): EquipmentCatalog[] {
    const f = filter.toLowerCase();
    return this.allEquipment.filter(equipment =>
      equipment.sku.toLowerCase().includes(f) || equipment.model.toLowerCase().includes(f)
    );
  }

  private generateStatusForm(status?: RmaStatus): FormGroup {
    return this.fb.group({
      date: [status?.date, Validators.required],
      type: [status?.type, Validators.required],
      description: [status?.description],
      timeSpentInHours: [status?.timeSpentInHours],
    });
  }

  private generateLoanedItemForm(l?: LoanedItem): FormGroup {
    const formGroup = this.fb.group({
      serialNumber: [l?.serialNumber, Validators.required],
      manufacturer: [l?.manufacturer || this.rma.manufacturer, Validators.required],
      sku: [l?.sku || this.rma.sku, Validators.required],
      model: [l?.model || this.rma.model, Validators.required],
      dispatchDate: [l?.dispatchDate],
      returnedDate: [l?.returnedDate],
    });
    this.filteredEquipment = merge()
      .pipe(
        startWith(''),
        map(state => (state ? this._filterEquipment(state).slice(0, 20) : [])),
      );
    return formGroup;
  }

  private updateDisabledFields() {
    const field = this.editForm.controls['isOnHold'];
    if (this.rma.resolvedStatus === RmaResolvedStatus.COMPLETE) {
      field.disable();
    } else {
      field.enable();
    }
  }

  private refreshClient() {
    if (!this._selectedClientId) {
      this.currentClient = null;
      this.updateAvailablePeople();
      return;
    }
    this.clientService.get(this._selectedClientId).subscribe({
      next: client => {
        this.currentClient = client;
        this.updateAvailablePeople();
      },
      error: err => {
        console.warn("SingleRmaComponent failed to load client", this._selectedClientId, err);
        this.currentClient = null;
        this.updateAvailablePeople();
      },
    });
  }

  private updateAvailablePeople() {
    const fromClient = this.currentClient?.people ?? [];
    const cp = this.editForm.controls['contactPerson']?.value as Person | null;
    if (cp && !this.findMatchingPerson(fromClient, cp)) {
      this.availablePeople = [cp, ...fromClient];
    } else {
      this.availablePeople = fromClient;
    }
  }

  private findMatchingPerson(list: Person[], target: Person): Person | undefined {
    return list.find(p => this.comparePeople(p, target));
  }

}
